import { useState, useEffect } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HardDrive, Trash2, RotateCcw, Download, FolderOpen } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Shell } from '../components/layout/Shell';
import { Button } from '../components/ui/Button';
import { ProfileEditSection } from '../components/community/ProfileEditSection';
import { usePreferences, type Theme, type DefaultView, type ItemsPerPage } from '../hooks/usePreferences';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import type { TranslationKey } from '../i18n/en';
import { apiClient } from '../api/client';
import { useAppMode } from '../context/AppModeContext';
import { THEMES, THEME_DEFINITIONS, AI_PROVIDERS, type AiProvider, type ThemeGroup } from '@siliconvault/shared';
import { Route as rootRoute } from './__root';
import styles from './settings.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});

const viewOptions: DefaultView[] = ['grid', 'table'];
const pageOptions: ItemsPerPage[] = [10, 20, 50];


function SettingsPage() {
  const { preferences, setPreference } = usePreferences();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const { mode } = useAppMode();

  const [licenseInfo, setLicenseInfo] = useState<{ email: string; activated_at?: string } | null>(null);
  const [showChangeLicense, setShowChangeLicense] = useState(false);

  function refreshLicenseInfo() {
    if (mode === 'desktop' && (window as any).__TAURI_INTERNALS__) {
      import('@tauri-apps/api/core').then(({ invoke }) =>
        invoke<{ email: string; activated_at?: string }>('get_license_info')
          .then(setLicenseInfo)
          .catch(() => {})
      );
    }
  }

  useEffect(() => {
    refreshLicenseInfo();
  }, [mode]);

  const themeLabelKeys: Record<Theme, TranslationKey> = {
    green_phosphor: 'settings.themeGreenPhosphor',
    amber_phosphor: 'settings.themeAmberPhosphor',
    commodore: 'settings.themeCommodore',
    atari: 'settings.themeAtari',
    spectrum: 'settings.themeSpectrum',
    amstrad: 'settings.themeAmstrad',
    amiga: 'settings.themeAmiga',
    vu_meter: 'settings.themeVuMeter',
    brushed_aluminum: 'settings.themeBrushedAluminum',
    darkroom: 'settings.themeDarkroom',
    chrome_leather: 'settings.themeChromeLeather',
    reel_to_reel: 'settings.themeReelToReel',
    vinyl_groove: 'settings.themeVinylGroove',
    tube_glow: 'settings.themeTubeGlow',
    midnight_dial: 'settings.themeMidnightDial',
    guilloche: 'settings.themeGuilloche',
    kodachrome: 'settings.themeKodachrome',
    film_noir: 'settings.themeFilmNoir',
    cyanotype: 'settings.themeCyanotype',
    patina: 'settings.themePatina',
    rose_gold: 'settings.themeRoseGold',
    lume: 'settings.themeLume',
    modern_dark: 'settings.themeModernDark',
    modern_light: 'settings.themeModernLight',
    vivid: 'settings.themeVivid',
  };

  const groupOrder: ThemeGroup[] = ['Retro Computers', 'Retro Audio / Hi-Fi', 'Vintage Cameras', 'Watches', 'General'];
  const themeGroups = (() => {
    const grouped = new Map<ThemeGroup, Array<{ id: Theme; name: string; gradient: string }>>();
    for (const id of THEMES) {
      const def = THEME_DEFINITIONS[id];
      const c = def.colors;
      const entry = {
        id: id as Theme,
        name: t(themeLabelKeys[id]),
        gradient: `linear-gradient(135deg, ${c['--bg-primary']}, ${c['--bg-solid']}, ${c['--accent']})`,
      };
      if (!grouped.has(def.group)) grouped.set(def.group, []);
      grouped.get(def.group)!.push(entry);
    }
    return groupOrder.filter((g) => grouped.has(g)).map((g) => ({ label: g, themes: grouped.get(g)! }));
  })();

  return (
    <Shell>
      <div className={styles.page}>
        <h1 className={styles.title}>{t('settings.title')}</h1>

        {/* Profile */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.profile')}</h2>
          <div className={styles.profileGrid}>
            <ProfileForm
              currentEmail={user?.email ?? ''}
              currentName={user?.displayName ?? ''}
              onSaved={refreshUser}
            />
            {mode !== 'desktop' && <PasswordForm />}
          </div>
        </div>

        {/* Public Profile */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('community.publicProfile')}</h2>
          <ProfileEditSection
            bio={user?.bio ?? ''}
            location={user?.location ?? ''}
            profileSlug={user?.profileSlug ?? null}
            avatarUrl={user?.avatarUrl ?? null}
            displayName={user?.displayName ?? ''}
          />
        </div>

        {/* AI Configuration */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>AI Configuration</h2>
          <AiConfigForm />
        </div>

        {/* Pro Subscription */}
        {mode === 'web' && <ProSubscriptionSection />}

        {/* License — desktop only */}
        {mode === 'desktop' && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>License</h2>
            <div className={styles.profileCard}>
              {licenseInfo ? (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Licensed to</span>
                    <span>{licenseInfo.email}</span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Activated on</span>
                    <span>
                      {licenseInfo.activated_at
                        ? new Date(licenseInfo.activated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                        : '—'}
                    </span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Support & updates</span>
                    <span>
                      {licenseInfo.activated_at
                        ? `Included until ${new Date(new Date(licenseInfo.activated_at).setFullYear(new Date(licenseInfo.activated_at).getFullYear() + 1)).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`
                        : 'Included for 12 months from activation'}
                    </span>
                  </div>
                  <p className={styles.licenseNote}>
                    Your license provides unlimited access to SiliconVault Desktop. Support and software updates are included for 12 months from the date of activation.
                  </p>
                  {!showChangeLicense ? (
                    <div className={styles.formActions} style={{ marginTop: '1rem' }}>
                      <Button size="small" variant="secondary" onClick={() => setShowChangeLicense(true)}>
                        Change License
                      </Button>
                    </div>
                  ) : (
                    <ChangeLicenseForm
                      onSuccess={(info) => { setLicenseInfo(info); setShowChangeLicense(false); }}
                      onCancel={() => setShowChangeLicense(false)}
                    />
                  )}
                </>
              ) : (
                <p className={styles.licenseNote}>License information unavailable.</p>
              )}
            </div>
          </div>
        )}

        {/* Backup & Restore — desktop only */}
        {mode === 'desktop' && <BackupRestoreSection />}

        {/* Inventory */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.inventory')}</h2>
          <div className={styles.profileCard}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('settings.defaultView')}</span>
              <div className={styles.optionGroup}>
                {viewOptions.map((v) => (
                  <button
                    key={v}
                    className={`${styles.optionButton} ${preferences.defaultView === v ? styles.optionButtonActive : ''}`}
                    onClick={() => setPreference('defaultView', v)}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('settings.itemsPerPage')}</span>
              <div className={styles.optionGroup}>
                {pageOptions.map((n) => (
                  <button
                    key={n}
                    className={`${styles.optionButton} ${preferences.itemsPerPage === n ? styles.optionButtonActive : ''}`}
                    onClick={() => setPreference('itemsPerPage', n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('settings.showTicker')}</span>
              <div className={styles.optionGroup}>
                <button
                  className={`${styles.optionButton} ${preferences.showTicker ? styles.optionButtonActive : ''}`}
                  onClick={() => setPreference('showTicker', true)}
                >
                  {t('settings.tickerOn')}
                </button>
                <button
                  className={`${styles.optionButton} ${!preferences.showTicker ? styles.optionButtonActive : ''}`}
                  onClick={() => setPreference('showTicker', false)}
                >
                  {t('settings.tickerOff')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.appearance')}</h2>
          <div className={styles.themeTileGrid}>
            {themeGroups.map((group) => (
              <div key={group.label} className={styles.themeTile}>
                <div className={styles.themeTileLabel}>{group.label}</div>
                <div className={styles.themeTileSwatches}>
                  {group.themes.map((th) => (
                    <button
                      key={th.id}
                      className={`${styles.themeSwatch} ${preferences.theme === th.id ? styles.themeSwatchActive : ''}`}
                      onClick={() => setPreference('theme', th.id)}
                      title={th.name}
                    >
                      <div
                        className={styles.themeSwatchPreview}
                        style={{ background: th.gradient }}
                      />
                      <span className={styles.themeSwatchName}>{th.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop promo — web only */}
        {mode !== 'desktop' && (
          <div className={styles.desktopPromoBanner}>
            <span className={styles.desktopPromoText}>{t('settings.desktopPromoText')}</span>
            <Link to="/download" className={styles.desktopPromoLink}>{t('settings.desktopPromoCta')}</Link>
          </div>
        )}
      </div>
    </Shell>
  );
}

// --- Backup & Restore Section (desktop only) ---

interface BackupEntry {
  name: string;
  sizeBytes: number;
  createdAt: string;
  migrationVersion: number | null;
}

function BackupRestoreSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Track which backup has a pending confirm action
  const [confirmAction, setConfirmAction] = useState<{ type: 'delete' | 'restore'; name: string } | null>(null);

  const { data: backupsData, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () => apiClient.listBackups(),
  });

  const backups: BackupEntry[] = backupsData?.data ?? [];
  const currentMigrationVersion: number = backupsData?.currentMigrationVersion ?? 0;

  const createMutation = useMutation({
    mutationFn: () => apiClient.createBackup(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups'] });
      setMessage({ type: 'success', text: t('settings.backupCreated') });
    },
    onError: () => setMessage({ type: 'error', text: t('settings.backupFailed') }),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => apiClient.deleteBackup(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups'] });
      setConfirmAction(null);
      setMessage({ type: 'success', text: t('settings.backupDeleted') });
    },
    onError: () => {
      setConfirmAction(null);
      setMessage({ type: 'error', text: 'Failed to delete backup.' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (name: string) => apiClient.restoreBackup(name),
    onSuccess: () => {
      setConfirmAction(null);
      setMessage({ type: 'success', text: t('settings.backupRestored') });
    },
    onError: () => {
      setConfirmAction(null);
      setMessage({ type: 'error', text: t('settings.restoreFailed') });
    },
  });

  function handleConfirm() {
    if (!confirmAction) return;
    setMessage(null);
    if (confirmAction.type === 'delete') {
      deleteMutation.mutate(confirmAction.name);
    } else {
      restoreMutation.mutate(confirmAction.name);
    }
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const busy = deleteMutation.isPending || restoreMutation.isPending;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('settings.backupRestore')}</h2>
      <div className={styles.profileCard}>
        <p className={styles.licenseNote} style={{ marginTop: 0, marginBottom: '1rem' }}>
          {t('settings.backupDesc')}
        </p>

        <div className={styles.formActions} style={{ marginBottom: '1.25rem' }}>
          <Button
            size="small"
            disabled={createMutation.isPending}
            onClick={() => { setMessage(null); setConfirmAction(null); createMutation.mutate(); }}
          >
            <Download size={14} style={{ marginRight: '0.375rem', verticalAlign: '-2px' }} />
            {createMutation.isPending ? t('settings.creatingBackup') : t('settings.createBackup')}
          </Button>
          <Button
            size="small"
            variant="secondary"
            onClick={() => {
              import('@tauri-apps/api/core').then(({ invoke }) =>
                invoke('open_data_folder').catch(() => {})
              );
            }}
          >
            <FolderOpen size={14} style={{ marginRight: '0.375rem', verticalAlign: '-2px' }} />
            Open Folder
          </Button>
          {message && (
            <span className={`${styles.formMessage} ${message.type === 'success' ? styles.formSuccess : styles.formError}`}>
              {message.text}
            </span>
          )}
        </div>

        {/* Inline confirmation banner */}
        {confirmAction && (() => {
          const backupEntry = backups.find((b) => b.name === confirmAction.name);
          const isOlderSchema =
            confirmAction.type === 'restore' &&
            backupEntry?.migrationVersion != null &&
            currentMigrationVersion > 0 &&
            backupEntry.migrationVersion < currentMigrationVersion;

          return (
            <div className={styles.backupConfirm}>
              <p className={styles.backupConfirmText}>
                {confirmAction.type === 'delete' ? t('settings.confirmDelete') : t('settings.confirmRestore')}
              </p>
              {isOlderSchema && (
                <p className={styles.backupSchemaWarning}>
                  {t('settings.schemaWarning', {
                    backupVersion: String(backupEntry!.migrationVersion),
                    currentVersion: String(currentMigrationVersion),
                  })}
                </p>
              )}
              <div className={styles.formActions}>
                <Button size="small" disabled={busy} onClick={handleConfirm}>
                  {busy
                    ? (confirmAction.type === 'delete' ? t('settings.deleteBackup') : t('settings.restoringBackup'))
                    : (confirmAction.type === 'delete' ? t('settings.deleteBackup') : t('settings.restoreBackup'))}
                </Button>
                <Button size="small" variant="secondary" disabled={busy} onClick={() => setConfirmAction(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          );
        })()}

        {isLoading ? (
          <p className={styles.licenseNote}>Loading...</p>
        ) : backups.length === 0 ? (
          <p className={styles.licenseNote}>{t('settings.noBackups')}</p>
        ) : (
          <div className={styles.backupList}>
            {backups.map((b) => (
              <div key={b.name} className={styles.backupRow}>
                <div className={styles.backupInfo}>
                  <HardDrive size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <div>
                    <div className={styles.backupName}>{b.name}</div>
                    <div className={styles.backupMeta}>
                      {formatSize(b.sizeBytes)} &middot;{' '}
                      {new Date(b.createdAt).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
                <div className={styles.backupActions}>
                  <button
                    className={styles.backupActionBtn}
                    title={t('settings.restoreBackup')}
                    disabled={busy}
                    onClick={() => { setMessage(null); setConfirmAction({ type: 'restore', name: b.name }); }}
                  >
                    <RotateCcw size={14} />
                  </button>
                  <button
                    className={`${styles.backupActionBtn} ${styles.backupActionDanger}`}
                    title={t('settings.deleteBackup')}
                    disabled={busy}
                    onClick={() => { setMessage(null); setConfirmAction({ type: 'delete', name: b.name }); }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Change License Form (desktop only) ---

function ChangeLicenseForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: (info: { email: string; activated_at?: string }) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!email || !key) { setError('Email and license key are required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const info = await invoke<{ email: string; activated_at?: string }>('change_license', { email, key });
      onSuccess(info);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)' }}>
      <div className={styles.profileCardTitle}>Enter new license</div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Email</label>
        <input
          className={styles.formInput}
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); }}
          placeholder="licensed@example.com"
          autoComplete="off"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>License Key</label>
        <input
          className={styles.formInput}
          type="text"
          value={key}
          onChange={(e) => { setKey(e.target.value.trim()); setError(null); }}
          placeholder="Paste your license key here"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {error && (
        <p className={styles.formError} style={{ marginBottom: '0.75rem' }}>{error}</p>
      )}

      <div className={styles.formActions}>
        <Button size="small" disabled={saving} onClick={handleSave}>
          {saving ? 'Activating…' : 'Activate'}
        </Button>
        <Button size="small" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// --- Profile Form ---

function ProfileForm({
  currentEmail,
  currentName,
  onSaved,
}: {
  currentEmail: string;
  currentName: string;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(currentEmail);
  const [displayName, setDisplayName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sync fields when user data loads
  const [lastEmail, setLastEmail] = useState(currentEmail);
  const [lastName, setLastName] = useState(currentName);
  if (currentEmail !== lastEmail) {
    setEmail(currentEmail);
    setLastEmail(currentEmail);
  }
  if (currentName !== lastName) {
    setDisplayName(currentName);
    setLastName(currentName);
  }

  const hasChanges = email !== currentEmail || displayName !== currentName;

  async function handleSave() {
    if (!hasChanges) return;
    setSaving(true);
    setMessage(null);

    const updates: { email?: string; displayName?: string } = {};
    if (email !== currentEmail) updates.email = email;
    if (displayName !== currentName) updates.displayName = displayName;

    try {
      await apiClient.updateProfile(updates);
      await onSaved();
      setMessage({ type: 'success', text: t('settings.profileUpdated') });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : t('settings.failedToUpdateProfile');
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.profileCard}>
      <div className={styles.profileCardTitle}>{t('settings.accountDetails')}</div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('settings.displayName')}</label>
        <input
          className={styles.formInput}
          type="text"
          value={displayName}
          onChange={(e) => { setDisplayName(e.target.value); setMessage(null); }}
          maxLength={100}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('settings.email')}</label>
        <input
          className={styles.formInput}
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setMessage(null); }}
        />
      </div>

      <div className={styles.formActions}>
        <Button
          size="small"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          {saving ? t('settings.saving') : t('settings.saveChanges')}
        </Button>
        {message && (
          <span className={`${styles.formMessage} ${message.type === 'success' ? styles.formSuccess : styles.formError}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
}


// --- AI Config Form (desktop only) ---

interface AiConfigResponse {
  configured: boolean;
  provider?: AiProvider;
  endpoint?: string;
  deployment?: string;
  apiVersion?: string;
  model?: string;
  apiKeyMasked?: string;
}

function AiConfigForm() {
  const qc = useQueryClient();
  const [provider, setProvider] = useState<AiProvider>('openai');
  const [apiKey, setApiKey] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [deployment, setDeployment] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [model, setModel] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: current } = useQuery<AiConfigResponse>({
    queryKey: ['ai-settings'],
    queryFn: () => fetch('/api/settings/ai', { credentials: 'include' }).then((r) => r.json()),
  });

  // Sync form fields from saved config when it loads
  useEffect(() => {
    if (current?.configured) {
      if (current.provider) setProvider(current.provider as AiProvider);
      if (current.endpoint) setEndpoint(current.endpoint);
      if (current.deployment) setDeployment(current.deployment);
      if (current.apiVersion) setApiVersion(current.apiVersion);
      if (current.model) setModel(current.model);
    }
  }, [current?.configured, current?.provider]);

  const saveMutation = useMutation({
    mutationFn: (body: object) =>
      fetch('/api/settings/ai', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).message);
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-settings'] });
      qc.invalidateQueries({ queryKey: ['app-mode'] });
      setApiKey('');
      setMessage({ type: 'success', text: 'AI configuration saved.' });
    },
    onError: (e: Error) => setMessage({ type: 'error', text: e.message }),
  });

  const clearMutation = useMutation({
    mutationFn: () =>
      fetch('/api/settings/ai', { method: 'DELETE', credentials: 'include' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-settings'] });
      qc.invalidateQueries({ queryKey: ['app-mode'] });
      setMessage({ type: 'success', text: 'AI configuration removed.' });
    },
  });

  const needsEndpoint = provider === 'azure' || provider === 'custom';

  function handleSave() {
    setMessage(null);
    if (!apiKey) { setMessage({ type: 'error', text: 'API key is required.' }); return; }
    if (needsEndpoint && !endpoint) { setMessage({ type: 'error', text: 'Endpoint is required for this provider.' }); return; }
    saveMutation.mutate({
      provider,
      apiKey,
      endpoint: endpoint || undefined,
      deployment: deployment || undefined,
      apiVersion: apiVersion || undefined,
      model: model || undefined,
    });
  }

  return (
    <div className={styles.profileCard}>
      <div className={styles.profileCardTitle}>
        {current?.configured
          ? `Configured — ${current.provider} (key: ${current.apiKeyMasked})`
          : 'Not configured'}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Provider</label>
        <div className={styles.optionGroup}>
          {AI_PROVIDERS.map((p) => (
            <button
              key={p}
              className={`${styles.optionButton} ${provider === p ? styles.optionButtonActive : ''}`}
              onClick={() => setProvider(p)}
              type="button"
            >
              {p === 'openai' ? 'OpenAI' : p === 'azure' ? 'Azure OpenAI' : p === 'gemini' ? 'Gemini' : 'Custom'}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>API Key</label>
        <input
          className={styles.formInput}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={current?.configured ? `Current: ${current.apiKeyMasked}` : 'sk-...'}
          autoComplete="off"
        />
      </div>

      {needsEndpoint && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            {provider === 'azure' ? 'Azure Endpoint' : 'Endpoint URL'}
          </label>
          <input
            className={styles.formInput}
            type="url"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder={
              provider === 'azure'
                ? 'https://your-resource.openai.azure.com'
                : 'https://api.example.com/v1'
            }
          />
        </div>
      )}

      {provider === 'azure' && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Deployment name</label>
            <input
              className={styles.formInput}
              type="text"
              value={deployment}
              onChange={(e) => setDeployment(e.target.value)}
              placeholder={current?.deployment ?? 'gpt-4o'}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>API version</label>
            <input
              className={styles.formInput}
              type="text"
              value={apiVersion}
              onChange={(e) => setApiVersion(e.target.value)}
              placeholder={current?.apiVersion ?? '2024-02-01'}
            />
          </div>
        </>
      )}

      {provider === 'gemini' && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Model</label>
          <input
            className={styles.formInput}
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="gemini-2.5-flash"
          />
          <span className={styles.formHint} style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
            Leave empty for default (gemini-2.5-flash)
          </span>
        </div>
      )}

      <div className={styles.formActions}>
        <Button
          size="small"
          disabled={saveMutation.isPending}
          onClick={handleSave}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {current?.configured && (
          <Button
            size="small"
            variant="secondary"
            disabled={clearMutation.isPending}
            onClick={() => { setMessage(null); clearMutation.mutate(); }}
          >
            {clearMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        )}
        {message && (
          <span className={`${styles.formMessage} ${message.type === 'success' ? styles.formSuccess : styles.formError}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
}

// --- Pro Subscription Section ---

function ProSubscriptionSection() {
  const { aiTier } = useAppMode();

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{aiTier === 'pro' ? 'Pro Subscription' : 'Your Plan'}</h2>
      <div className={styles.profileCard}>
        <div className={styles.profileCardTitle}>
          {aiTier === 'pro' ? 'Active — Pro Plan' : 'Free Plan'}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          {aiTier === 'pro'
            ? 'Unlimited items, showroom, AI analysis, and PDF export.'
            : 'Up to 100 items, 5 showroom slots. Upgrade to unlock more.'}
        </p>
        <div style={{ marginTop: '1rem' }}>
          <Link to="/upgrade">
            <Button size="small" variant={aiTier === 'pro' ? 'secondary' : 'primary'}>
              {aiTier === 'pro' ? 'Manage Plan' : 'View Plans & Upgrade'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// --- Password Form ---

function PasswordForm() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const passwordsMatch = newPassword === confirmNewPassword;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8 && passwordsMatch;
  const showMismatch = confirmTouched && confirmNewPassword.length > 0 && !passwordsMatch;

  async function handleSave() {
    if (!canSubmit) return;
    setSaving(true);
    setMessage(null);

    try {
      await apiClient.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setConfirmTouched(false);
      setMessage({ type: 'success', text: t('settings.passwordChanged') });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : t('settings.failedToChangePassword');
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.profileCard}>
      <div className={styles.profileCardTitle}>{t('settings.changePassword')}</div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('settings.currentPassword')}</label>
        <input
          className={styles.formInput}
          type="password"
          value={currentPassword}
          onChange={(e) => { setCurrentPassword(e.target.value); setMessage(null); }}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('settings.newPassword')}</label>
        <input
          className={styles.formInput}
          type="password"
          value={newPassword}
          onChange={(e) => { setNewPassword(e.target.value); setMessage(null); }}
          minLength={8}
          placeholder={t('login.placeholderPassword')}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('settings.confirmNewPassword')}</label>
        <input
          className={`${styles.formInput} ${showMismatch ? styles.formInputError : ''}`}
          type="password"
          value={confirmNewPassword}
          onChange={(e) => { setConfirmNewPassword(e.target.value); setMessage(null); }}
          onBlur={() => setConfirmTouched(true)}
          placeholder={t('login.placeholderPassword')}
        />
        {showMismatch && (
          <span className={styles.formError}>{t('settings.passwordsDoNotMatch')}</span>
        )}
      </div>

      <div className={styles.formActions}>
        <Button
          size="small"
          disabled={!canSubmit || saving}
          onClick={handleSave}
        >
          {saving ? t('settings.changing') : t('settings.changePassword')}
        </Button>
        {message && (
          <span className={`${styles.formMessage} ${message.type === 'success' ? styles.formSuccess : styles.formError}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
}
